import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Text } from '@react-three/drei';
import * as THREE from 'three';
import { easing } from 'maath';

// --- SHARED GEOMETRIES (Optimization) ---
const GEO = {
    hitBox: new THREE.BoxGeometry(3, 3.5, 3),
    pivot: new THREE.SphereGeometry(0.15, 16, 16),
    hanger: new THREE.CylinderGeometry(0.04, 0.04, 1.2),
    seat: new THREE.BoxGeometry(0.9, 0.1, 0.4),
    // Passenger parts
    pBody: new THREE.CylinderGeometry(0.1, 0.2, 0.4, 8),
    pHead: new THREE.SphereGeometry(0.12, 8, 8),
    // Cabin parts
    body: new THREE.CylinderGeometry(1, 0.8, 1.5, 32),
    cage: new THREE.CylinderGeometry(1.02, 0.82, 1.52, 16), // Lower poly for wireframe
    roof: new THREE.ConeGeometry(1.2, 0.4, 32),
    roofRim: new THREE.TorusGeometry(1.1, 0.02, 8, 32),
    ring: new THREE.TorusGeometry(1.05, 0.02, 8, 32),
};

// Rotate rings once to avoid runtime rotation if possible, but they need specific orientation.
// We'll keep rotation in the component for flexibility unless expensive.

// Low-poly passenger component - Uses shared geometry
const Passenger = ({ position, color }) => (
    <group position={position}>
        <mesh position={[0, 0.2, 0]} geometry={GEO.pBody}>
            <meshStandardMaterial color={color} roughness={0.5} />
        </mesh>
        <mesh position={[0, 0.55, 0]} geometry={GEO.pHead}>
            <meshStandardMaterial color="#ffccaa" roughness={0.5} />
        </mesh>
    </group>
);

const NEON_PALETTE = ['#00F5FF', '#FF0095', '#FFD700', '#4E2FB3', '#70FF00']; // Reusing provided palette

const Cabin = ({ position, category, isActive, wheelRotationRef, onClick, isLocked }) => {
    const groupRef = useRef();

    useFrame((state, delta) => {
        if (groupRef.current && wheelRotationRef.current) {
            // Counter-rotate to stay upright
            const targetRot = -wheelRotationRef.current.rotation.z;
            easing.dampE(groupRef.current.rotation, [0, 0, targetRot], 0.1, delta);
        }
    });

    const rimColor = category.color;

    // Stable random colors
    const p1Color = NEON_PALETTE[(category.id * 2) % NEON_PALETTE.length];
    const p2Color = NEON_PALETTE[(category.id * 2 + 1) % NEON_PALETTE.length];

    return (
        <group ref={groupRef} position={position}>
            <Float speed={isActive ? 2 : 0} rotationIntensity={0.2} floatIntensity={0.2}>
                {/* Hit Box */}
                <mesh
                    visible={true}
                    onClick={(e) => {
                        e.stopPropagation();
                        document.body.style.cursor = 'auto';
                        if (onClick) onClick();
                    }}
                    onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
                    onPointerOut={() => { document.body.style.cursor = 'auto'; }}
                    geometry={GEO.hitBox}
                >
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} color="red" />
                </mesh>

                {/* --- NEW CABIN DESIGN START --- */}

                {/* Pivot Joint */}
                <mesh position={[0, 1.2, 0]} geometry={GEO.pivot}>
                    <meshStandardMaterial color="#222" metalness={0.8} />
                </mesh>
                {/* Hanger Rod */}
                <mesh position={[0, 0.6, 0]} geometry={GEO.hanger}>
                    <meshStandardMaterial color="#aaa" metalness={1} roughness={0.2} />
                </mesh>

                {/* Main Cabin Group */}
                <group position={[0, -0.2, 0]}>

                    {/* BENCH SEAT */}
                    <mesh position={[0, -0.35, -0.1]} geometry={GEO.seat}>
                        <meshStandardMaterial color="#111" roughness={0.8} />
                    </mesh>

                    {/* PASSENGERS */}
                    <Passenger position={[-0.3, -0.2, 0]} color={p1Color} />
                    <Passenger position={[0.3, -0.2, 0]} color={p2Color} />

                    {/* NEON RING LIGHTS */}
                    <mesh position={[0, 0.3, 0]} rotation={[Math.PI / 2, 0, 0]} geometry={GEO.ring}>
                        <meshBasicMaterial color={rimColor} toneMapped={false} />
                    </mesh>
                    <mesh position={[0, -0.3, 0]} rotation={[Math.PI / 2, 0, 0]} geometry={GEO.ring}>
                        <meshBasicMaterial color={rimColor} toneMapped={false} />
                    </mesh>

                    <Text
                        position={[0, 1.6, 0]}
                        fontSize={0.4}
                        color={rimColor}
                        anchorX="center"
                        anchorY="middle"
                        outlineWidth={0.02}
                        outlineColor="#000000"
                    >
                        {category.name.toUpperCase()}
                        <meshBasicMaterial toneMapped={false} />
                    </Text>

                    {/* Gondola Body */}
                    <mesh geometry={GEO.body}>
                        <meshPhysicalMaterial
                            color={isActive ? category.color : "#ffffff"}
                            metalness={0.1}
                            roughness={0}
                            transmission={0.9} // Glass effect
                            thickness={0.5}
                            ior={1.5}
                            transparent
                            opacity={1}
                            emissive={isActive ? category.color : "#000000"}
                            emissiveIntensity={isActive ? 0.8 : 0}
                        />
                    </mesh>

                    {/* Neon Wireframe Cage */}
                    <mesh geometry={GEO.cage}>
                        <meshBasicMaterial
                            color={rimColor}
                            wireframe
                            transparent
                            opacity={isActive ? 0.8 : 0.3}
                            toneMapped={false}
                        />
                    </mesh>

                    {/* Roof Chrome */}
                    <mesh position={[0, 0.95, 0]} geometry={GEO.roof}>
                        <meshStandardMaterial color="#333" metalness={1} roughness={0.1} />
                    </mesh>

                    {/* Roof Neon Rim */}
                    <mesh position={[0, 0.75, 0]} rotation={[Math.PI / 2, 0, 0]} geometry={GEO.roofRim}>
                        <meshBasicMaterial color={rimColor} toneMapped={false} />
                    </mesh>

                    {/* Active Glow Light */}
                    {isActive && (
                        <pointLight distance={6} intensity={4} color={rimColor} />
                    )}

                    {/* Soft Ambient Light */}
                    {!isActive && (
                        <pointLight distance={2} intensity={0.5} color={rimColor} />
                    )}
                </group>
                {/* --- NEW CABIN DESIGN END --- */}

            </Float>
        </group>
    );
};

export default Cabin;
